import React, { Component } from "react";
import { each, filter, flatten } from "underscore";
import { FormattedMessage, injectIntl } from "react-intl";

import "wxrecall/components/WeatherSearchInput.css";
import ReactTags from "react-tag-autocomplete";

import "wxrecall/components/CFPSTags.css";

import {
  convertMapToDict,
  getClone,
  mergeDictIntoMap,
  scrollToResultsTop
} from "wxrecall/util/util";

import CFPSTag from "wxrecall/components/CFPSTag";
import ProductInput from "wxrecall/components/ProductInput";
import ImageInput from "wxrecall/components/ImageInput";
import PredefinedRecall from "wxrecall/components/PredefinedRecall";
import RadiusInput from "wxrecall/components/RadiusInput";
import CollapseDuplicatesInput from "wxrecall/components/CollapseDuplicatesInput";
import SearchInputControls from "wxrecall/components/SearchInputControls";
import { productChoices } from "wxrecall/util/DefaultStates";
import { defaultSettings } from "wxrecall/util/defaults";
import DefaultStates from "wxrecall/util/DefaultStates";

const settings = window.settings || defaultSettings;

/**
 * Left panel main component for weather recall.
 * See also BulletinSearchInput for bulletin recall left panel.
 */
export class WeatherSearchInput extends Component {
  constructor(props) {
    super(props);

    this.superRestoreDefaults = props.restoreDefaults ?? (() => void 0)

    this.state = {
      networkRequestStates: {},
      tags: DefaultStates.tags,
      radius: DefaultStates.radius,
      collapseDuplicates: DefaultStates.collapseDuplicates,
      alphaProducts: DefaultStates.alphaProducts,
      imageProducts: DefaultStates.imageProducts,
      predefinedRecalls: window.predefinedRecalls || [],
      isLoading: false,
      selectedRecall: null,
      selectedRecallChanged: false
    };

    this.updateRequestState = this.props.updateRequestState;
    this.onPredefinedRecallChange = this.onPredefinedRecallChange.bind(this);
    this.networkManager = this.props.networkManager;
    this.updateRecall = this.updateRecall.bind(this);
    this.deleteRecall = this.deleteRecall.bind(this);

    // We want to capture when any keys are pressed on this page.
    this.component = React.createRef();
    window.onkeydown = this.onKeyDown.bind(this);
  }

  componentDidMount = () => {
    if (window.predefinedRecallLoad) {
      const recalls = window.predefinedRecalls.filter(
        recall => recall.value === window.predefinedRecallLoad
      );
      if (recalls) {
        this.onPredefinedRecallChange(recalls[0]);
        this.scheduleSearch = true;
      }
    }
  };

  componentDidUpdate = () => {
    if (this.scheduleSearch) {
      this.search();
      this.scheduleSearch = false;
    }
  };

  onRadiusShowChanged = () => {
    this.setState({
      selectedRecallChanged: true,
      radius: {
        ...this.state.radius,
        on: !this.state.radius.on
      }
    });
  };

  onRadiusValueChanged = e => {
    this.setState({
      selectedRecallChanged: true,
      radius: {
        ...this.state.radius,
        value: e.target.value
      }
    });
  };

  onCollapseDuplicateChanged = e => {
    const enabled = !this.state.collapseDuplicates.enabled;
    this.setState({
      selectedRecallChanged: true,
      collapseDuplicates: {
        enabled: enabled
      }
    });
    this.props.setCollapseDuplicates(enabled);
  };

  onAlphaToggle = product => {
    // Create a clone of the alpha products
    let newAlphaProducts = new Map([...this.state.alphaProducts]);

    // Update the targeted product
    newAlphaProducts.set(product, {
      ...this.state.alphaProducts.get(product),
      enabled: !this.state.alphaProducts.get(product).enabled
    });

    this.setState({
      selectedRecallChanged: true,
      alphaProducts: newAlphaProducts
    });
  };

  onChoiceChange = (product, e) => {
    const newAlphaProducts = getClone(this.state.alphaProducts);

    newAlphaProducts.set(product, {
      ...this.state.alphaProducts.get(product),
      value: e.target.value
    });

    this.setState({
      selectedRecallChanged: true,
      alphaProducts: newAlphaProducts
    });
  };

  onImageToggle = key => {
    let newImageProducts = getClone(this.state.imageProducts);
    let leaf = newImageProducts;

    // Step through until we reach the end of the composite key
    for (let keyPart of key.split("-")) {
      leaf = leaf.get(keyPart);
    }

    leaf.enabled = !leaf.enabled;
    this.setState({
      selectedRecallChanged: true,
      imageProducts: newImageProducts
    });
  };

  onKeyDown = e => {
    // If we got an enter key
    if (
      e.key === "Enter" &&
      !e.defaultPrevented &&
      this.component.current.contains(window.document.activeElement)
    ) {
      this.search();
    }
  };

  getQueryString = (
    tags,
    radiusState,
    selectedAlphas,
    selectedImages,
    productData
  ) => {
    let siteString = tags
      .map(tag => `site=${tag.name}`)
      .join("&");

    let alphaString = selectedAlphas.length
      ? "alpha=" + selectedAlphas.join("&alpha=")
      : "";
    let imageString = selectedImages.length
      ? "image=" + selectedImages.join("&image=")
      : "";

    let queryString =
      "?" +
      [siteString, alphaString, imageString]
        .filter(string => string)
        .join("&");

    // We only search for radius if it's on, has a value, and is not disabled
    if (radiusState.on && radiusState.value && !radiusState.disabled) {
      queryString += `&radius=${radiusState.value}`;
    }

    for (let i = 0; i < selectedAlphas.length; ++i) {
      // Check if choice value exists
      const product = selectedAlphas[i];
      let value = productData.get(product).value;

      // Check if a value should exist but does not
      if (
        value === undefined &&
        productChoices[product] &&
        productChoices[product].length
      ) {
        value = productChoices[product][0].value;
      }
      if (value) {
        queryString += `&${product}_choice=${value}`;
      }
    }
    return queryString;
  };

  getSelectedImages = (images, path) => {
    images = images ? images : this.state.imageProducts;
    return filter(
      flatten(
        [...images].map(([k, v]) => {
          let key = (path ? [path, k].join("/") : k).toUpperCase();
          if ("enabled" in v) {
            return v.enabled ? (v.keys ? v.keys : [key]) : null;
          } else {
            return this.getSelectedImages(v, key);
          }
        }, this),
        v => {
          return v != null;
        },
        this
      )
    );
  };

  // Actually search for alphanumeric weather
  search = () => {
    // Clear out any existing search result data
    this.props.searchCallback([]);

    const selectedAlphas = [...this.state.alphaProducts]
      .map(([k, v]) => {
        return v.enabled ? k : null;
      })
      .filter(i => {
        return i != null;
      });
    const selectedImages = this.getSelectedImages();

    // Update the App state with the current Notam Language and collapseDuplicate choices.
    this.props.setPreferredLanguage(
      this.state.alphaProducts.get("notam").value
    );
    this.props.setCollapseDuplicates(this.state.collapseDuplicates.enabled);

    const selectedCount = selectedAlphas.length + selectedImages.length;
    // We don't need to search if we don't have any tags or don't have any products selected
    if (this.state.tags.length > 0 && selectedCount > 0) {
      const queryString = this.getQueryString(
        this.state.tags,
        this.state.radius,
        selectedAlphas,
        selectedImages,
        this.state.alphaProducts
      );
      this.networkManager.requests
        .weatherSearch(queryString)
        .then(data => {
          for(let message of data.meta.messages){
            if(message.message === 'alpha.geomInvalid'){
              for(let site of message.details){
                for(let tag of this.state.tags){
                  if (tag.name === site){
                    tag.error = true
                  }
                }
              }
            }
          }

          data.searchParameters = {
            radius: this.state.radius,
            tags: this.state.tags
          };
          // Give that data to the parent component so it can display updated results
          this.props.searchCallback(data);
          scrollToResultsTop();
        })
        .catch(error =>
          console.error("Error while querying for data: " + error)
        );
    } else {
      console.warn(
        "User tried to search without any tags or without any products"
      );
    }
  };

  // Save the user's recall
  save = recall => {
    const recallData = {
      tags: this.state.tags,
      radius: this.state.radius,
      collapseDuplicates: this.state.collapseDuplicates,
      alphaProducts: convertMapToDict(this.state.alphaProducts),
      imageProducts: convertMapToDict(this.state.imageProducts)
    };
    const recallName = recall ? recall.label : null;
    if (recallName) {
      this.setState({ isLoading: true, selectedRecall: recall });
      this.networkManager.requests
        .save({
          data: JSON.stringify({
            name: recallName,
            recall: recallData
          })
        })
        .then(response => {
          // Check to see if this was a new item or not
          if (response.newRecallCreated) {
            // New item, so we add it to our frontend list
            this.setState({
              selectedRecallChanged: false,
              isLoading: false,
              predefinedRecalls: [
                ...this.state.predefinedRecalls,
                { value: recallName, label: recallName, data: recallData }
              ]
            });
            this.props.addMessage({
              text: <FormattedMessage id="alpha.predefinedRecallSaveSuccess" />
            });
          } else {
            // Update, so we need to find and then update the predefined recall that just succeeded
            // saving. Start by creating a clone of the previous recalls
            let newPredefinedRecalls = JSON.parse(
              JSON.stringify(this.state.predefinedRecalls)
            );
            for (let i = 0; i < newPredefinedRecalls.length; ++i) {
              if (newPredefinedRecalls[i].label === recallName) {
                newPredefinedRecalls[i] = {
                  value: recallName,
                  label: recallName,
                  data: recallData
                };
                break;
              }
            }
            this.setState({
              isLoading: false,
              predefinedRecalls: newPredefinedRecalls,
              selectedRecallChanged: false
            });
            this.props.addMessage({
              text: (
                <FormattedMessage id="alpha.predefinedRecallUpdateSuccess" />
              )
            });
          }
        })
        .catch(error => {
          console.error("Error while saving recall: " + error);
          this.props.addMessage({
            type: "error",
            text: <FormattedMessage id="alpha.predefinedRecallSaveFail" />
          });
          this.setState({ isLoading: false });
        });
    } else {
      console.error("User tried to save a recall without a name");
    }
  };

  onPredefinedRecallChange = selectedRecall => {
    // If the user just cleared the current selection, the selected recall will be null
    if (selectedRecall == null) {
      this.setState({ selectedRecall: null });
    } else {
      // If this is a new recall that was just created, instead of selecting a previous one
      if (selectedRecall.__isNew__) {
        this.save(selectedRecall);
      } else {
        // Start with a blank object
        let newState = {};

        // The default state also implicitly contains the list of keys that represent a predefined
        // recall, so we can dynamically populate a new state based on that dictionary
        each(
          DefaultStates,
          (defaultValue, key) => {
            if (defaultValue instanceof Map) {
              // We serialize Maps a little differently, because javascript's `JSON.*` functions don't
              // handle nested Maps well

              // Start with a clone of the existing state
              newState[key] = getClone(this.state[key]);

              // Recursively merge the dictionary values into the map structure. Modifies it in-place
              mergeDictIntoMap(newState[key], selectedRecall.data[key]);
            } else {
              // Basic handling; use data from recall if it is defined otheriwse use the default
              newState[key] =
                selectedRecall.data[key] === undefined
                  ? defaultValue
                  : selectedRecall.data[key];
            }
          },
          this
        );

        // Support predefined recalls that existed before the preferred language/wind settings existed:
        for (let [k, v] of DefaultStates.alphaProducts) {
          if (
            v.value !== undefined &&
            newState.alphaProducts.get(k).value === undefined
          ) {
            newState.alphaProducts.get(k).value = v.value;
          }
        }

        this.setState({
          ...newState,
          selectedRecall: selectedRecall,
          selectedRecallChanged: false
        });
      }
    }
  };

  handleDelete(i) {
    const tags = this.state.tags.slice(0);
    tags.splice(i, 1);
    const queryingPolygons = tags.filter(tag => tag.polygon).length > 0;
    this.setState({
      selectedRecallChanged: true,
      tags,
      radius: {
        ...this.state.radius,
        disabled: queryingPolygons
      }
    });
  }

  handleAddition(tag) {
    tag.name = tag.name.toUpperCase()
    tag.error = false
    const tags = [].concat(this.state.tags, tag);
    const queryingPolygons = tags.filter(tag => tag.polygon).length > 0;
    this.setState({
      selectedRecallChanged: true,
      tags,
      radius: {
        ...this.state.radius,
        disabled: queryingPolygons
      }
    });
  }

  /** handleRestoreDefaults()
   *  - Sets the component state to have the default weather search parameters
   */
  handleRestoreDefaults() {
    this.superRestoreDefaults()
    this.setState({
      ...DefaultStates,
      ...{ selectedRecall: null }
    });
  }

  /** setState()
   *  - Updates the persistent settings service & then sets component state
   * @param {*} state
   */
  setState(state) {
    super.setState(state);
  }

  updateRecall = () => {
    this.save(this.state.selectedRecall);
  };

  deleteRecall = () => {
    const currentRecallName = this.state.selectedRecall
      ? this.state.selectedRecall.label
      : null;
    if (currentRecallName) {
      this.setState({ isLoading: true, selectedRecall: null });
      this.networkManager.requests
        .delete({
          data: JSON.stringify({
            name: currentRecallName
          })
        })
        .then(response => {
          // Check to see if we actually deleted any records
          if (response.numRecordsDeleted > 0) {
            // If so, we need to filter it out from the frontend
            let newPredefinedRecalls = this.state.predefinedRecalls.filter(
              predefinedRecall => {
                return predefinedRecall.label !== currentRecallName;
              }
            );
            this.setState({
              predefinedRecalls: newPredefinedRecalls,
              isLoading: false
            });
            this.props.addMessage({
              text: (
                <FormattedMessage id="alpha.predefinedRecallDeleteSuccess" />
              )
            });
          } else {
            console.warn("Failed trying to delete predefined recall");
            this.setState({ isLoading: false });
            this.props.addMessage({
              type: "error",
              text: <FormattedMessage id="alpha.predefinedRecallDeleteFail" />
            });
          }
        })
        .catch(error => {
          console.error("Error while deleting predefined recall: " + error);
          this.setState({ isLoading: false });
        });
    } else {
      // Kinda weird, but for now just log it
      console.warn("User tried to delete a non-existent predefined recall");
    }
  };

  render() {
    const savedRecalls = settings.isLoggedIn ? (
      <PredefinedRecall
        list={this.state.predefinedRecalls}
        selected={this.state.selectedRecall}
        isModified={this.state.selectedRecallChanged}
        isLoading={this.state.isLoading}
        onChange={this.onPredefinedRecallChange}
        update={this.updateRecall}
        delete={this.deleteRecall}
      />
    ) : null;

    return (
      <div ref={this.component} className="alpha-search-input">
        <div className="primary-inputs">
          {savedRecalls}
          <ReactTags
            tags={this.state.tags}
            handleDelete={this.handleDelete.bind(this)}
            handleAddition={this.handleAddition.bind(this)}
            addOnBlur={true}
            placeholder={this.props.intl.formatMessage({
              id: "new_search_item"
            })}
            tagComponent={CFPSTag}
            delimiters={[9, 13, 32]}
            allowNew={true}
          />
          <SearchInputControls
            onSearch={this.search}
            onRestoreDefaults={this.handleRestoreDefaults.bind(this)}
            helpUrl={settings.helpUrlWxRecall}
          />
          <RadiusInput
            on={this.state.radius.on}
            disabled={this.state.radius.disabled}
            value={this.state.radius.value}
            onRadiusShowChanged={this.onRadiusShowChanged}
            onRadiusValueChanged={this.onRadiusValueChanged}
          />
          <CollapseDuplicatesInput
            onCollapseDuplicateChanged={this.onCollapseDuplicateChanged}
            enabled={this.state.collapseDuplicates.enabled}
          />
        </div>
        <div className="product-inputs">
          {[...this.state.alphaProducts].map(([product, data]) => {
            const choices = productChoices[product];
            return (
              <ProductInput
                product={product}
                onProductToggle={this.onAlphaToggle.bind(this, product)}
                productState={this.state.alphaProducts.get(product)}
                choices={choices}
                onChoiceChange={this.onChoiceChange.bind(this, product)}
                key={product}
              />
            );
          }, this)}
          <hr />
          <div className={"image-inputs"}>
            {[...this.state.imageProducts].map(([id, data]) => {
              return (
                <ImageInput
                  key={id}
                  parent={null}
                  id={id}
                  name={id}
                  data={data}
                  onToggle={this.onImageToggle.bind(this)}
                />
              );
            }, this)}
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(WeatherSearchInput);
